import { BaseEffect } from '../BaseEffect.js';

/**
 * Sync failure effect implementation
 * Simulates CRT monitor vertical sync failure with rolling, tearing, and glitching
 */
export class SyncFailureEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            rollSpeed: 0.5,        // Speed of vertical rolling
            rollAmount: 0.3,       // Amount of vertical displacement
            tearIntensity: 0.4,    // Intensity of horizontal tearing
            tearCount: 3.0,        // Number of tear lines
            glitchAmount: 0.3,     // Amount of glitch displacement
            glitchSpeed: 1.0,      // Speed of glitch changes
            scanlineIntensity: 0.1, // Intensity of scanlines
            noiseAmount: 0.15,     // Amount of noise in tears
            animate: true          // Whether to animate the effect
        };

        super('sync-failure', shaderManager, defaultParams);
    }

    async init() {
        const vertexSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentSource = `
            precision highp float;
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform float u_rollSpeed;
            uniform float u_rollAmount;
            uniform float u_tearIntensity;
            uniform float u_tearCount;
            uniform float u_glitchAmount;
            uniform float u_glitchSpeed;
            uniform float u_scanlineIntensity;
            uniform float u_noiseAmount;
            
            varying vec2 v_texcoord;

            // Random function
            float random(vec2 st) {
                return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
            }

            // Noise function
            float noise(vec2 st) {
                vec2 i = floor(st);
                vec2 f = fract(st);
                
                float a = random(i);
                float b = random(i + vec2(1.0, 0.0));
                float c = random(i + vec2(0.0, 1.0));
                float d = random(i + vec2(1.0, 1.0));

                vec2 u = f * f * (3.0 - 2.0 * f);
                return mix(a, b, u.x) + (c - a)* u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
            }

            void main() {
                vec2 uv = v_texcoord;
                float time = u_time;
                
                // Calculate aspect ratio
                float aspect = u_resolution.x / u_resolution.y;
                
                // Vertical rolling
                float roll = sin(time * u_rollSpeed) * u_rollAmount;
                uv.y = fract(uv.y + roll);
                
                // Horizontal tears
                float tearTime = time * u_glitchSpeed;
                float tearNoise = noise(vec2(tearTime * 0.1, uv.y * u_tearCount));
                float tear = step(0.5, tearNoise) * u_tearIntensity;
                
                // Apply horizontal displacement for tears
                uv.x += tear * (noise(vec2(tearTime, uv.y)) * 2.0 - 1.0);
                
                // Glitch displacement
                float glitchNoise = noise(vec2(floor(time * u_glitchSpeed * 5.0), floor(uv.y * 20.0)));
                vec2 glitchOffset = vec2(
                    (glitchNoise * 2.0 - 1.0) * u_glitchAmount,
                    0.0
                );
                
                // Apply glitch offset with probability
                if (random(vec2(floor(time * u_glitchSpeed), floor(uv.y * 10.0))) > 0.96) {
                    uv += glitchOffset;
                }
                
                // Add noise to torn areas
                float staticNoise = random(uv + vec2(time * 10.0));
                
                // Sample the image with distorted coordinates
                vec4 color = texture2D(u_image, fract(uv));
                
                // Add noise in tear areas
                color.rgb = mix(color.rgb, vec3(staticNoise), tear * u_noiseAmount);
                
                // Add scanlines
                float scanline = sin(uv.y * u_resolution.y * 0.5 - time * 5.0) * 0.5 + 0.5;
                color.rgb *= 1.0 - (scanline * u_scanlineIntensity);
                
                // Add subtle color shifting in tear areas
                if (tear > 0.0) {
                    color.r = texture2D(u_image, fract(uv + vec2(0.01, 0.0))).r;
                    color.b = texture2D(u_image, fract(uv - vec2(0.01, 0.0))).b;
                }
                
                gl_FragColor = color;
            }
        `;

        await super.init(vertexSource, fragmentSource);
    }

    setupUniforms() {
        super.setupUniforms();
        
        this.addUniform('u_rollSpeed', { type: '1f' });
        this.addUniform('u_rollAmount', { type: '1f' });
        this.addUniform('u_tearIntensity', { type: '1f' });
        this.addUniform('u_tearCount', { type: '1f' });
        this.addUniform('u_glitchAmount', { type: '1f' });
        this.addUniform('u_glitchSpeed', { type: '1f' });
        this.addUniform('u_scanlineIntensity', { type: '1f' });
        this.addUniform('u_noiseAmount', { type: '1f' });
    }

    updateUniforms(uniforms) {
        super.updateUniforms(uniforms);
        
        Object.assign(uniforms, {
            u_rollSpeed: { value: this.params.rollSpeed, type: '1f' },
            u_rollAmount: { value: this.params.rollAmount, type: '1f' },
            u_tearIntensity: { value: this.params.tearIntensity, type: '1f' },
            u_tearCount: { value: this.params.tearCount, type: '1f' },
            u_glitchAmount: { value: this.params.glitchAmount, type: '1f' },
            u_glitchSpeed: { value: this.params.glitchSpeed, type: '1f' },
            u_scanlineIntensity: { value: this.params.scanlineIntensity, type: '1f' },
            u_noiseAmount: { value: this.params.noiseAmount, type: '1f' }
        });

        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    getUIConfig() {
        return {
            rollSpeed: {
                min: 0,
                max: 2,
                step: 0.1,
                label: 'Roll Speed'
            },
            rollAmount: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Roll Amount'
            },
            tearIntensity: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Tear Intensity'
            },
            tearCount: {
                min: 1,
                max: 10,
                step: 1,
                label: 'Tear Count'
            },
            glitchAmount: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Glitch Amount'
            },
            glitchSpeed: {
                min: 0,
                max: 3,
                step: 0.1,
                label: 'Glitch Speed'
            },
            scanlineIntensity: {
                min: 0,
                max: 0.5,
                step: 0.01,
                label: 'Scanline Intensity'
            },
            noiseAmount: {
                min: 0,
                max: 0.5,
                step: 0.01,
                label: 'Noise Amount'
            },
            animate: {
                label: 'Animate'
            }
        };
    }
} 